import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Crown, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";

import { consumerAccountsEnabled, useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";

const WHAT_IF_PRESETS = [1, 5, 10, 20];

export const premiumPersonalManagerEnabled = () => (
  consumerAccountsEnabled()
  && process.env.REACT_APP_PREMIUM_PERSONAL_MANAGER_ENABLED === "true"
);

export default function PremiumYourOdds({ competitionId }) {
  const auth = useConsumerAuth();
  const owner = auth?.account?.user?.user_id || "guest";
  return <OwnedYourOdds key={`${owner}:${competitionId}:${auth?.account?.subscription?.entitlement_active}`} competitionId={competitionId} />;
}

function OwnedYourOdds({ competitionId }) {
  const auth = useConsumerAuth();
  const enabled = premiumPersonalManagerEnabled();
  const entitled = auth?.account?.subscription?.entitlement_active === true;
  const [form, setForm] = useState({ tickets_bought: "", amount_spent: "" });
  const [customTickets, setCustomTickets] = useState("");
  const [projection, setProjection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);
  const request = useRef(0);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const loadProjection = async (tickets) => {
    const sequence = ++request.current;
    setError("");
    const query = tickets ? `?tickets=${encodeURIComponent(tickets)}` : "";
    let data;
    try {
      ({ data } = await consumerApi.get(
        `/premium/your-odds/${encodeURIComponent(competitionId)}${query}`,
      ));
    } catch (reason) {
      if (active.current && sequence === request.current) {
        setError("Your Odds could not be loaded. Your saved entry has not been changed.");
      }
      throw reason;
    }
    if (!active.current || sequence !== request.current) return;
    setProjection(data);
    if (!tickets) setLoaded(true);
    if (!tickets && data.record) {
      setForm({
        tickets_bought: String(data.record.tickets_bought),
        amount_spent: data.record.amount_spent,
      });
    }
  };

  useEffect(() => {
    if (!enabled || !auth?.ready || !entitled || !competitionId) return;
    loadProjection().catch(() => {});
  // competitionId and entitlement changes intentionally reset the private view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.ready, competitionId, enabled, entitled]);

  if (!enabled || !auth?.ready) return null;
  if (!auth.account || !entitled) {
    return (
      <section className="mt-8 border-y border-white/15 py-5 text-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline text-[var(--cc-gold)]">Competition Checker Premium</p>
            <h2 className="mt-2 font-display text-2xl font-semibold">Your Odds</h2>
            <p className="mt-2 text-sm text-[var(--cc-text-muted)]">
              Record your tickets privately and compare current and maximum-pool odds.
            </p>
          </div>
          <Link className="btn-gold rounded px-4 py-2 text-sm" to={auth.account ? "/account/subscription" : "/signin"}>
            {auth.account ? "View Premium" : "Sign in for Premium"}
          </Link>
        </div>
      </section>
    );
  }

  const save = async (event) => {
    event.preventDefault();
    if (!loaded || saving) return;
    setSaving(true);
    try {
      await auth.mutate(
        "put",
        `/premium/personal-competitions/${encodeURIComponent(competitionId)}`,
        {
          tickets_bought: Number(form.tickets_bought),
          amount_spent: form.amount_spent,
        },
      );
      await loadProjection();
      toast.success("Competition saved to your private list.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save this competition.");
    } finally {
      setSaving(false);
    }
  };

  const whatIf = (tickets) => {
    loadProjection(tickets).catch(() => {});
  };

  const submitCustomWhatIf = (event) => {
    event.preventDefault();
    const tickets = Number(customTickets);
    if (!Number.isInteger(tickets) || tickets < 1 || tickets > 100000) {
      toast.error("Enter between 1 and 100,000 tickets.");
      return;
    }
    whatIf(tickets);
  };

  return (
    <section className="mt-8 border-y border-white/15 py-5 text-white" data-testid="premium-your-odds">
      <div className="overline inline-flex items-center gap-2 text-[var(--cc-gold)]">
        <Crown size={14} fill="currentColor" /> Competition Checker Premium
      </div>
      <div className="mt-3 grid gap-6 lg:grid-cols-[1fr_1.15fr]">
        <div>
          <h2 className="font-display text-2xl font-semibold">Your Odds</h2>
          <p className="mt-2 text-sm leading-relaxed text-[var(--cc-text-muted)]">
            Keep a private record of tickets bought and what you spent. This is information, not a spending target.
          </p>
          <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={save}>
            <label className="text-sm">
              Tickets bought
              <input className="mt-2 w-full rounded border border-white/15 bg-white/5 px-3 py-2" type="number" min="1" max="100000" required value={form.tickets_bought} onChange={(event) => setForm({ ...form, tickets_bought: event.target.value })} />
            </label>
            <label className="text-sm">
              Amount spent
              <span className="mt-2 flex rounded border border-white/15 bg-white/5"><span className="px-3 py-2 text-white/50">£</span><input aria-label="Amount spent in pounds" className="min-w-0 flex-1 bg-transparent px-1 py-2 outline-none" type="number" min="0" max="1000000" step="0.01" required value={form.amount_spent} onChange={(event) => setForm({ ...form, amount_spent: event.target.value })} /></span>
            </label>
            <button className="btn-gold inline-flex min-h-10 items-center justify-center gap-2 rounded px-4 py-2 text-sm sm:col-span-2 sm:w-fit" disabled={saving || !loaded}>
              <Save size={16} /> {saving ? "Saving..." : "Save privately"}
            </button>
          </form>
          {error && <div className="mt-3 flex items-center gap-3"><p role="alert" className="text-sm text-[var(--cc-text-muted)]">{error}</p><button type="button" aria-label="Retry Your Odds" title="Retry Your Odds" className="min-h-10 min-w-10 shrink-0 rounded border border-white/20 p-2" onClick={() => loadProjection().catch(() => {})}><RefreshCw size={18} /></button></div>}
        </div>
        <div className="border-t border-white/10 pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          <div className="flex items-center gap-2"><Calculator size={18} className="text-[var(--cc-gold)]" /><h3 className="font-display text-lg font-semibold">What if I buy...</h3></div>
          <div className="mt-3 flex flex-wrap gap-2">
            {WHAT_IF_PRESETS.map((tickets) => <button key={tickets} type="button" className="min-h-10 rounded border border-white/20 px-3 text-sm hover:border-[var(--cc-gold)]" onClick={() => whatIf(tickets)}>{tickets} {tickets === 1 ? "ticket" : "tickets"}</button>)}
          </div>
          <form className="mt-3 flex max-w-sm flex-wrap gap-2" onSubmit={submitCustomWhatIf}>
            <label className="min-w-0 flex-1 text-sm">
              <span className="sr-only">Custom what-if ticket count</span>
              <input aria-label="Custom what-if ticket count" className="min-h-10 w-full rounded border border-white/15 bg-white/5 px-3 py-2" inputMode="numeric" min="1" max="100000" placeholder="Custom ticket count" type="number" value={customTickets} onChange={(event) => setCustomTickets(event.target.value)} />
            </label>
            <button className="min-h-10 rounded border border-white/20 px-3 text-sm hover:border-[var(--cc-gold)]" type="submit">Calculate</button>
          </form>
          {projection?.status === "available" ? (
            <dl className="mt-5 grid gap-3 sm:grid-cols-2">
              <div><dt className="text-xs uppercase text-white/45">Your current odds</dt><dd className="mt-1 font-mono-num text-xl">{projection.current_odds?.display || "Unavailable"}</dd></div>
              <div><dt className="text-xs uppercase text-white/45">Your maximum-pool odds</dt><dd className="mt-1 font-mono-num text-xl">{projection.maximum_pool_odds?.display || "Unavailable"}</dd></div>
            </dl>
          ) : (
            <p className="mt-5 text-sm text-[var(--cc-text-muted)]">{projection?.message || "Choose a ticket amount to calculate Your Odds."}</p>
          )}
          {projection?.status === "available" && projection.message && <p className="mt-3 text-sm text-[var(--cc-text-muted)]">{projection.message}</p>}
          {projection?.observed_at && <p className="mt-3 text-xs text-[var(--cc-text-muted)]">Observed {new Date(projection.observed_at).toLocaleString("en-GB")}</p>}
          {projection?.status === "available" && <p className="mt-4 text-xs leading-relaxed text-[var(--cc-text-muted)]">{projection.disclosure}</p>}
        </div>
      </div>
    </section>
  );
}
